<template>
  <div class="d-flex flex-row align-center ga-1 my-1">
    <positive-integer-text-field
      v-model.number.lazy="priceRangeStart"
      :suffix="getCurrencySymbol(currency)"
      :aria-label="$t('priceRange.priceRangeFrom')"
      @change="checkPriceRangeStart"
      @keydown.enter="emitUpdate"
    />
    <sf-text>{{ $t('to') }}</sf-text>
    <positive-integer-text-field
      v-model.number.lazy="priceRangeEnd"
      :suffix="getCurrencySymbol(currency)"
      :aria-label="$t('priceRange.priceRangeTo')"
      @change="checkPriceRangeEnd"
      @keydown.enter="emitUpdate"
    />
    <sf-icon-button
      :icon="mdiSync"
      :aria-label="$t('priceRange.applyPriceRange')"
      @click="emitUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import SfIconButton from '@/components/button/SfIconButton.vue'
import { isPositiveInteger } from '@/components/filter/helpers'
import PositiveIntegerTextField from '@/components/filter/PositiveIntegerTextField.vue'
import { PriceRangeFilter } from '@/components/filter/types'
import SfText from '@/components/text/SfText.vue'
import { getCurrencySymbol } from '@/helpers'
import helpers from '@/modules/message/helpers'
import { mdiSync } from '@mdi/js'
import { PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  priceRangeFilter: {
    type: Object as PropType<PriceRangeFilter>,
    required: true
  },
  currency: {
    type: String,
    required: true
  }
})

const priceRangeStart = ref<number | undefined>()
const priceRangeEnd = ref<number | undefined>()
const emit = defineEmits<{
  update: [PriceRangeFilter]
}>()

const emitUpdate = () => {
  emit('update', { start: priceRangeStart.value, end: priceRangeEnd.value })
}

watch(
  () => props.priceRangeFilter,
  () => {
    priceRangeStart.value = props.priceRangeFilter.start
    priceRangeEnd.value = props.priceRangeFilter.end
  }
)

const checkPriceRangeStart = () => {
  if (priceRangeStart.value && !isPositiveInteger(priceRangeStart.value)) {
    helpers.reportError(t('onlyWholePositiveNumbers'))
    priceRangeStart.value = undefined
  }

  if (
    priceRangeEnd.value &&
    priceRangeStart.value &&
    priceRangeEnd.value <= priceRangeStart.value
  ) {
    helpers.reportError(t('priceRange.invalidPriceRange'))
    priceRangeStart.value = undefined
  }
}

const checkPriceRangeEnd = () => {
  if (priceRangeEnd.value && !isPositiveInteger(priceRangeEnd.value)) {
    helpers.reportError(t('onlyWholePositiveNumbers'))
    priceRangeEnd.value = undefined
  }

  if (
    priceRangeEnd.value &&
    priceRangeStart.value &&
    priceRangeEnd.value <= priceRangeStart.value
  ) {
    helpers.reportError(t('priceRange.invalidPriceRange'))
    priceRangeEnd.value = undefined
  }
}
</script>
